/**
 * Complete SEO Solution for Next.js
 * Everything in one file - no need for multiple files
 */

import Head from 'next/head';

/**
 * Generate default SEO values from config data
 * @param {Object} configData - Config data from Strapi
 * @returns {Object} - Default SEO values
 */
const getDefaultSEO = (configData = {}) => {
  const siteName = configData?.siteName || 'HireGo';
  const siteDescription = configData?.siteDescription || 'Professional minibus hire services with driver. Book your minibus or coach hire with HireGo.co.uk.';
  const siteKeywords = configData?.siteKeywords || 'minibus hire, minibus with driver, coach hire, airport transfers, HireGo, hirego.co.uk';
  const siteUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk';
  const siteImage = configData?.siteImage || {
    url: 'https://images.hirego.co.uk.s3.eu-west-2.amazonaws.com/hirego-cms/Hire_Go_Minibuses_Logo_65214f666e.png',
    alternativeText: 'HireGo - Minibus Hire Services'
  };

  return {
    metaTitle: `${siteName} | Top Rated Minibus Hire Company`,
    metaDescription: `${siteDescription}. Book a vehicle of any seating capacity from any brand, anytime you want.`,
    keywords: siteKeywords,
    metaRobots: 'index, follow',
    metaViewport: 'width=device-width, initial-scale=1.0',
    canonicalURL: siteUrl,
    metaImage: siteImage
  };
};

/**
 * Generate SEO meta tags object from Strapi data
 * @param {Object} seoData - SEO data from Strapi
 * @param {Object} pageData - Additional page data
 * @param {Object} configData - Global configuration data
 * @returns {Object} - Formatted SEO meta tags
 */
export function generateSEOTags(seoData = {}, pageData = {}, configData = {}) {
  // Get dynamic defaults from config data
  const defaultSEO = getDefaultSEO(configData);

  // Extract values with fallbacks
  const metaTitle = seoData?.metaTitle || pageData?.title || defaultSEO.metaTitle;
  const metaDescription = seoData?.metaDescription || pageData?.description || defaultSEO.metaDescription;
  const keywords = seoData?.keywords || defaultSEO.keywords;
  const metaRobots = seoData?.metaRobots || defaultSEO.metaRobots;
  const metaViewport = seoData?.metaViewport || defaultSEO.metaViewport;
  const canonicalURL = seoData?.canonicalURL || pageData?.url || defaultSEO.canonicalURL;
  const metaImage = seoData?.metaImage || defaultSEO.metaImage;
  const structuredData = seoData?.structuredData;
  const metaSocial = seoData?.metaSocial || [];

  // Generate Open Graph data
  const ogTitle = metaTitle;
  const ogDescription = metaDescription;
  const ogImage = metaImage?.url || defaultSEO.metaImage.url;
  const ogImageAlt = metaImage?.alternativeText || defaultSEO.metaImage.alternativeText;

  // Generate Twitter Card data
  const twitterTitle = metaTitle;
  const twitterDescription = metaDescription;
  const twitterImage = ogImage;
  const twitterImageAlt = ogImageAlt;

  return {
    title: metaTitle,
    description: metaDescription,
    keywords: keywords,
    robots: metaRobots,
    viewport: metaViewport,

    // Canonical URL
    canonical: canonicalURL,

    // Open Graph Tags
    openGraph: {
      title: ogTitle,
      description: ogDescription,
      type: 'website',
      url: canonicalURL,
      siteName: configData?.siteName || 'HireGo',
      images: [
        {
          url: ogImage,
          width: 1200,
          height: 630,
          alt: ogImageAlt,
        },
      ],
      locale: configData?.locale || 'en_GB',
    },

    // Twitter Card Tags
    twitter: {
      card: 'summary_large_image',
      title: twitterTitle,
      description: twitterDescription,
      images: [twitterImage],
      imageAlt: twitterImageAlt,
      site: configData?.twitterSite || '@hiregouk',
      creator: configData?.twitterCreator || '@hiregouk',
    },

    // Additional Meta Tags
    additionalMetaTags: [
      {
        name: 'author',
        content: configData?.siteName || defaultSEO.siteName,
      },
      {
        name: 'publisher',
        content: configData?.publisher || 'HireGo',
      },
      {
        name: 'theme-color',
        content: configData?.themeColor || '#ffffff',
      },
      {
        name: 'msapplication-TileColor',
        content: configData?.themeColor || '#ffffff',
      },
    ],

    // Favicon — only declare files that exist in public/
    icons: {
      icon: '/favicon.ico',
    },

    // Structured Data
    structuredData: structuredData,
  };
}

/**
 * Generate SEO for different page types
 */
export function getPageSEO(pageData = {}, configData = {}, pageUrl = '/') {
  const seoData = pageData?.seo || {};
  const siteUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk';

  // Override canonical URL with current page URL if needed
  if (pageUrl !== '/' && !seoData.canonicalURL) {
    seoData.canonicalURL = `${siteUrl}${pageUrl}`;
  }

  return generateSEOTags(seoData, pageData, configData);
}

export function getBlogSEO(blogData = {}, configData = {}, pageUrl = '/') {
  const siteUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk';
  const siteName = configData?.siteName || 'HireGo';
  
  const customSEO = {
    title: blogData.title || 'Blog Post',
    description: blogData.excerpt || blogData.description || '',
    canonical: `${siteUrl}${pageUrl}`,
  };
  
  return getPageSEO(blogData, configData, pageUrl, customSEO);
}

export function getCategorySEO(categoryData = {}, configData = {}, pageUrl = '/') {
  const siteUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk';
  const siteName = configData?.siteName || 'HireGo';
  
  const customSEO = {
    title: `${categoryData.name || 'Category'} - ${siteName}`,
    description: categoryData.description || `Find minibus hire services in ${categoryData.name || 'this category'}`,
    canonical: `${siteUrl}${pageUrl}`,
  };
  
  return getPageSEO(categoryData, configData, pageUrl, customSEO);
}

export function getCitySEO(cityData = {}, configData = {}, pageUrl = '/') {
  const siteUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk';
  const siteName = configData?.siteName || 'HireGo';
  
  const customSEO = {
    title: `Minibus Hire ${cityData.name || 'City'} - ${siteName}`,
    description: `Professional minibus hire services in ${cityData.name || 'your city'}. Book now for reliable transportation with driver.`,
    canonical: `${siteUrl}${pageUrl}`,
  };
  
  return getPageSEO(cityData, configData, pageUrl, customSEO);
}

export function getAuthorSEO(authorData = {}, configData = {}, pageUrl = '/') {
  const siteUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk';
  const siteName = configData?.siteName || 'HireGo';
  
  const customSEO = {
    title: `${authorData.name || 'Author'} - ${siteName}`,
    description: authorData.bio || `Read articles by ${authorData.name || 'this author'} on ${siteName}.`,
    canonical: `${siteUrl}${pageUrl}`,
  };
  
  return getPageSEO(authorData, configData, pageUrl, customSEO);
}

/**
 * SEO Head Component - Everything in one component
 */
export default function SEOHead({ pageData = {}, configData = {}, pageUrl = '/', customSEO = {} }) {
  // Generate base SEO data
  const seoConfig = getPageSEO(pageData, configData, pageUrl);
  
  // Apply custom overrides
  const finalSEO = {
    ...seoConfig,
    ...customSEO,
    openGraph: {
      ...seoConfig.openGraph,
      ...customSEO.openGraph,
    },
    twitter: {
      ...seoConfig.twitter,
      ...customSEO.twitter,
    }
  };

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{finalSEO.title}</title>
      <meta name="description" content={finalSEO.description} />
      <meta name="keywords" content={finalSEO.keywords} />
      <meta name="robots" content={finalSEO.robots} />
      <meta name="viewport" content={finalSEO.viewport} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalSEO.canonical} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={finalSEO.openGraph.title} />
      <meta property="og:description" content={finalSEO.openGraph.description} />
      <meta property="og:type" content={finalSEO.openGraph.type} />
      <meta property="og:url" content={finalSEO.openGraph.url} />
      <meta property="og:site_name" content={finalSEO.openGraph.siteName} />
      <meta property="og:image" content={finalSEO.openGraph.images[0]?.url} />
      <meta property="og:image:width" content={finalSEO.openGraph.images[0]?.width} />
      <meta property="og:image:height" content={finalSEO.openGraph.images[0]?.height} />
      <meta property="og:image:alt" content={finalSEO.openGraph.images[0]?.alt} />
      <meta property="og:locale" content={finalSEO.openGraph.locale} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={finalSEO.twitter.card} />
      <meta name="twitter:title" content={finalSEO.twitter.title} />
      <meta name="twitter:description" content={finalSEO.twitter.description} />
      <meta name="twitter:image" content={finalSEO.twitter.images} />
      <meta name="twitter:image:alt" content={finalSEO.twitter.imageAlt} />
      <meta name="twitter:site" content={finalSEO.twitter.site} />
      <meta name="twitter:creator" content={finalSEO.twitter.creator} />
      
      {/* Additional Meta Tags */}
      {finalSEO.additionalMetaTags?.map((tag, index) => (
        <meta key={index} name={tag.name} content={tag.content} />
      ))}
      
      {/* Favicon */}
      <link rel="icon" href={finalSEO.icons.icon} />
      {finalSEO.icons.apple?.map((icon, index) => (
        <link key={index} rel="apple-touch-icon" sizes={icon.sizes} href={icon.url} />
      ))}
      {finalSEO.icons.other?.map((icon, index) => (
        <link key={index} rel={icon.rel} href={icon.url} sizes={icon.sizes} type={icon.type} />
      ))}
      {finalSEO.manifest && <link rel="manifest" href={finalSEO.manifest} />}
      
      {/* Structured Data */}
      {finalSEO.structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(finalSEO.structuredData),
          }}
        />
      )}
    </Head>
  );
}
